import React, { useEffect, useState } from 'react';
import Comments from './Comments';
import StatusChip from './ui/StatusChip';
import ProgressBar from './ui/ProgressBar';
import EmptyState from './ui/EmptyState';
import CourseCover from './CourseCover';
import ImageQuote from './ui/ImageQuote';
import { IMAGES } from '../data/learningImagery';
import { QUOTES } from '../data/educationQuotes';
import { categoryFill } from '../data/categoryColours';
import './LearnerPortal.css';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function calcCourseProgress(course, progress) {
  const all  = course.modules.flatMap(m => m.lessons.map(l => l.id));
  const done = all.filter(id => progress[id]);
  return all.length ? Math.round((done.length / all.length) * 100) : 0;
}

function formatDate(value) {
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Access window: red only when it has really ended, amber when it needs action soon.
function AccessNotice({ invitedAt, expiresAt }) {
  if (!invitedAt && !expiresAt) return null;
  const expiry = expiresAt ? new Date(expiresAt) : new Date(new Date(invitedAt).getTime() + 60 * MS_PER_DAY);
  const daysLeft = Math.ceil((expiry - Date.now()) / MS_PER_DAY);
  if (daysLeft <= 0) {
    return (
      <p className="portal-access">
        <StatusChip kind="exception">Access expired</StatusChip>
        <span>Your access ended on {formatDate(expiry)}. Contact your administrator to renew it.</span>
      </p>
    );
  }
  return (
    <p className="portal-access">
      <StatusChip kind={daysLeft <= 14 ? 'action' : 'neutral'}>{daysLeft <= 14 ? 'Action required' : 'Access active'}</StatusChip>
      <span>Access until {formatDate(expiry)} ({daysLeft} day{daysLeft === 1 ? '' : 's'} left).</span>
    </p>
  );
}

// Matches the server's rules (server/assignments.js statusOf): completed wins,
// then overdue, then due within 7 days, then plain progress.
export function statusFor(course, pct, assignment) {
  if (pct >= 100) return { kind: 'done', label: 'Completed' };
  if (course.locked) {
    return { kind: 'pending', label: `Unlocks ${new Date(course.availableFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` };
  }
  if (assignment?.dueAt) {
    const left = new Date(assignment.dueAt).getTime() - Date.now();
    const label = assignment.kind === 'required' ? 'Required' : left < 0 ? 'Overdue' : 'Due soon';
    if (left < 0) return { kind: 'exception', label, dueState: 'Overdue' };
    if (left <= 7 * MS_PER_DAY) return { kind: 'action', label, dueState: 'Due soon' };
  }
  if (assignment?.kind === 'required') return { kind: 'action', label: 'Required' };
  return pct > 0 ? { kind: 'neutral', label: 'In progress' } : { kind: 'neutral', label: 'Not started' };
}

const URGENCY = { exception: 0, action: 1, neutral: 2, pending: 3, done: 4 };
const INSPIRATION_QUOTES = Object.entries(QUOTES);
const INSPIRATION_IMAGES = Object.values(IMAGES);

export function portalInspirationFor(random = Math.random, previousQuoteId = null) {
  const choices = INSPIRATION_QUOTES.filter(([id]) => id !== previousQuoteId);
  const [quoteId, quote] = choices[Math.floor(random() * choices.length)];
  const image = INSPIRATION_IMAGES[Math.floor(random() * INSPIRATION_IMAGES.length)];
  return { quoteId, quote, image };
}

function previousPortalQuoteId() {
  try {
    return window.sessionStorage.getItem('okiru_last_portal_quote');
  } catch (_) {
    return null;
  }
}

export default function LearnerPortal({ user, enrollments, progress, certificates, assignments = {}, navigateTo }) {
  const [courses, setCourses] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [inspiration] = useState(() => portalInspirationFor(Math.random, previousPortalQuoteId()));

  useEffect(() => {
    try {
      window.sessionStorage.setItem('okiru_last_portal_quote', inspiration.quoteId);
    } catch (_) {
      // The random selection still works when session storage is unavailable.
    }
  }, [inspiration.quoteId]);

  useEffect(() => {
    fetch(`/api/courses${user?.id ? `?userId=${user.id}` : ''}`)
      .then(r => r.json())
      .then(j => setCourses(j.data || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [user?.id]);

  const enrolled = courses.filter(c => enrollments.has(c.id));
  const rows = enrolled
    .map(course => {
      const pct = calcCourseProgress(course, progress);
      const assignment = assignments[course.id];
      return { course, pct, assignment, status: statusFor(course, pct, assignment) };
    })
    // What needs attention first: overdue, due soon, then the rest; soonest due date first.
    .sort((a, b) => {
      const byUrgency = URGENCY[a.status.kind] - URGENCY[b.status.kind];
      if (byUrgency) return byUrgency;
      const da = a.assignment?.dueAt ? new Date(a.assignment.dueAt).getTime() : Infinity;
      const db = b.assignment?.dueAt ? new Date(b.assignment.dueAt).getTime() : Infinity;
      return da - db;
    });

  const inProgress = rows.filter(r => r.pct > 0 && r.pct < 100);
  const completed  = rows.filter(r => r.pct >= 100);
  const needsAttention = rows.filter(r => r.status.kind === 'exception' || r.status.kind === 'action');
  const certCount  = Object.keys(certificates || {}).length;
  const next = rows.find(r => r.pct < 100 && !r.course.locked);

  const open = (course) => navigateTo('lesson', course.id, course.modules?.[0]?.id);

  return (
    <div className="portal">
      <div className="portal-head">
        <h2 className="portal-title">My learning</h2>
        <p className="portal-sub">{user.name}{user.company ? `, ${user.company}` : ''}</p>
      </div>

      <AccessNotice invitedAt={user.invitedAt} expiresAt={user.expiresAt} />

      <dl className="portal-summary">
        <div><dt>Needs attention</dt><dd>{needsAttention.length}</dd></div>
        <div><dt>In progress</dt><dd>{inProgress.length}</dd></div>
        <div><dt>Completed</dt><dd>{completed.length}</dd></div>
        <div><dt>Certificates</dt><dd>{certCount}</dd></div>
      </dl>

      <section className="portal-inspiration" aria-label="Learning inspiration">
        <ImageQuote image={inspiration.image} quote={inspiration.quote} layout="beside" />
      </section>

      {next && (
        <section className="portal-continue" aria-labelledby="continue-heading">
          <div>
            <h3 id="continue-heading" className="portal-section-title">
              {next.status.kind === 'exception' ? 'Overdue: finish this next' : next.pct > 0 ? 'Continue learning' : 'Start your next course'}
            </h3>
            <p className="portal-continue-name">{next.course.title}</p>
            <p className="portal-continue-meta">
              {[next.assignment?.dueAt ? `Due ${formatDate(next.assignment.dueAt)}` : null, next.course.duration, next.course.level].filter(Boolean).join(' | ')}
            </p>
          </div>
          {next.pct > 0 && <ProgressBar value={next.pct} label={`${next.course.title} progress`} />}
          <button type="button" className="btn btn-primary" onClick={() => open(next.course)}>
            {next.pct > 0 ? 'Continue' : 'Start course'}
          </button>
        </section>
      )}

      <section aria-labelledby="courses-heading">
        <h3 id="courses-heading" className="portal-section-title">Your courses</h3>
        {loaded && enrolled.length === 0 ? (
          <EmptyState
            image={IMAGES.learnerLaptop}
            title="You are not enrolled in any courses yet"
            actionLabel="Browse the course catalogue"
            onAction={() => navigateTo('catalogue')}
          >
            Choose a course from the catalogue to begin. Courses assigned to you will also appear here with a due date.
          </EmptyState>
        ) : !loaded ? (
          <p role="status">Loading your courses…</p>
        ) : (
          <div className="portal-course-grid">
            {rows.map(({ course, pct, assignment, status }) => (
              <article className="portal-course-card" key={course.id}>
                <CourseCover courseId={course.id} title={course.title} category={course.category} decorative compact />
                <div className="portal-course-body">
                  <div className="portal-course-heading">
                    <h4>{course.title}</h4>
                    <StatusChip kind={status.kind}>{status.label}</StatusChip>
                  </div>
                  <p className="portal-course-category">
                    <span className="cat-dot" style={{ background: categoryFill(course.category) }} aria-hidden="true" />
                    {course.category}
                    {course.level ? ` · ${course.level}` : ''}
                  </p>
                  <div className="portal-course-progress">
                    <ProgressBar value={pct} label={`${course.title} progress`} />
                    <span>{pct}% complete</span>
                  </div>
                  {assignment?.dueAt && (
                    <p className="portal-course-due">
                      {assignment.kind === 'required' ? 'Required · ' : ''}Due {formatDate(assignment.dueAt)}{status.dueState ? ` · ${status.dueState}` : ''}
                    </p>
                  )}
                  {!course.locked ? (
                    <button type="button" className="btn btn-ghost portal-course-action" onClick={() => open(course)}>
                      {pct >= 100 ? 'Review' : pct > 0 ? 'Continue' : 'Start'}
                      <span className="sr-only">: {course.title}</span>
                    </button>
                  ) : <p className="portal-course-due">Available from {formatDate(course.availableFrom)}</p>}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <Comments user={user} />
    </div>
  );
}
